import React from "react";
import { Link } from "react-router-dom";
import { WEEK_HIGHLIGHTS } from "./WelcomeCard";
import type { WeekHighlights } from "./WelcomeCard";

/** What a figure the backend has not counted shows in place of a number. */
const NO_FIGURE = "-";

/**
 * How wide one card is.
 *
 * Two cards and the edge of a third, at every phone width — the overhang is the
 * only thing telling the reader the row scrolls, because a touch screen draws
 * no scrollbar and this band has no arrows or dots.
 *
 * A flat 170px did not hold that. The page's gutter is 26px and the gaps are
 * 12px, so the third card starts at 26 + 170 + 12 + 170 + 12 = 390px — exactly
 * the edge of a 390px phone. The row looked like a finished pair and the third
 * count was gone. The width is worked out from the screen instead: the overhang
 * is `100vw - 26 - 2W - 24`, and asking it for 36px gives `W = 50vw - 43px`.
 *
 * Clamped at both ends rather than left as a plain share of the screen, which
 * is the part the fixed width was right about: a card holds a figure, a label
 * and a period, and a third of a small screen is not a column — at 136px
 * "clients renewal" wraps to three lines. 140px is where it still reads in two,
 * and 170px is as wide as the words ever need, so a big phone spends the extra
 * on a longer overhang instead of on air inside the card.
 */
const CARD_WIDTH = "w-[clamp(140px,50vw_-_43px,170px)]";

interface MobileWeekHighlightsProps {
  /** The week either side of today — see `WeekHighlights`. */
  highlights: WeekHighlights;
}

/**
 * The week a workspace has just had, as a phone reads it.
 *
 * The same three counts the wide screen writes across the welcome card, in the
 * shape a phone has room for. There they are thirds of a card: a 40px mark, the
 * figure and the label on one line, the period under it. At 360px a third of
 * that card is about 100px, which is not a column — the figure and its label
 * wrap into each other and the three read as one grey block rather than as
 * three facts.
 *
 * So each count gets a card of its own and the row scrolls sideways, which is
 * what buys the width back: a card is 38% of the screen rather than 33% of a
 * card, the figure can be the size of a figure, and the label sits under it on
 * its own line. The band is headed on the page rather than inside a surface —
 * "This week" is a section of the screen here, not a card with a title.
 *
 * The row bleeds to both edges of the screen while its first card still lines
 * up with the page's own 16px gutter, so the cards scroll off the edge the way
 * a phone's carousels do rather than inside a 328px window with air either
 * side.
 *
 * What the counts mean is not decided here: `WEEK_HIGHLIGHTS` is the wide
 * screen's own list, labels, marks and all, so the two screens cannot drift
 * into naming the same count differently.
 */
const MobileWeekHighlights: React.FC<MobileWeekHighlightsProps> = ({
  highlights,
}) => (
  <section>
    <h2 className="text-[16px] font-semibold leading-tight text-ink">
      This week
    </h2>

    {/* `snap-start` on the cards rather than free scrolling: three cards is a
        short run, and snapping is what stops it settling with a card half under
        the gutter. The scrollbar is taken off because the overhanging card
        already says the row moves, and on Android a bar under the cards reads
        as part of the band.

        `scroll-pl-page` is what keeps the first card on the page's own
        gutter. Without it the snap port starts at the scroller's border rather
        than inside its padding, so the browser settles the row a gutter's
        width along to line the first card's edge up with the screen's — and
        the card sits flush against the edge while the heading above it is
        indented. Measured, not guessed: the first card's left went 16 → 0 with
        the padding in place. */}
    <div className="mt-3 -mx-page flex snap-x snap-mandatory scroll-pl-page gap-3 overflow-x-auto px-page pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {WEEK_HIGHLIGHTS.map((item) => {
        const Mark = item.Mark;
        const count = highlights[item.id];
        // A week nobody has counted is a dash — see `WeekHighlights`. A week
        // with nothing in it is the word the list gives, where it gives one:
        // "No clients renewal" is the ordinary week, "0" is a figure to act on.
        const value =
          count === null
            ? NO_FIGURE
            : count === 0 && item.none
              ? item.none
              : count;

        const body = (
          <>
            {/* The disc is inside the artwork, so the mark is the whole thing
                rather than a glyph over a tinted circle. */}
            <Mark aria-hidden className="h-10 w-10 flex-shrink-0" />
            <div className="mt-4">
              <div className="text-[26px] font-semibold leading-none tracking-tight text-ink">
                {value}
              </div>
              {/* The list's labels are written to follow a figure on one line —
                  "1 new request". Standing on their own they start a line, so
                  they are capitalised here rather than written twice. */}
              <div className="mt-2 text-[15px] font-medium leading-tight text-ink first-letter:uppercase">
                {item.label}
              </div>
              <div className="mt-1 text-[12px] leading-tight text-ink-subtle">
                {item.note}
              </div>
            </div>
          </>
        );

        const shape = `${CARD_WIDTH} flex-shrink-0 snap-start card-surface border border-line bg-white p-4`;

        // The whole card is the target where the figure has a list behind it,
        // the way the wide screen makes the whole highlight one — somebody
        // reading "3 clients renewal" wants to know which three.
        return item.to ? (
          <Link
            key={item.id}
            to={item.to}
            className={`${shape} block transition-colors hover:bg-[#F7FBFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40`}
          >
            {body}
          </Link>
        ) : (
          <article key={item.id} className={shape}>
            {body}
          </article>
        );
      })}
    </div>
  </section>
);

export default MobileWeekHighlights;
